import { useEffect, useState } from "react";
import { logger } from "../lib/logger";
import { Gift, Users, DollarSign, TrendingUp, Microscope } from "lucide-react";
import { AppShell, PageLoader } from "../components/AppShell";
import { CopyBtn } from "../components/CopyBtn";
import { api } from "../lib/api";

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export default function ReferralsPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/referral/stats");
        setStats(data);
      } catch (err) {
        logger.error("Failed to load referral stats", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <PageLoader />;

  const origin = window.location.origin;
  const code = stats?.referral_code || "";
  const tryLink = code ? `${origin}/try?ref=${code}` : "";
  const signupLink = code ? `${origin}/register?ref=${code}` : "";
  const pct = stats?.commission_pct ?? 30;

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8" data-testid="referrals-page">
        <h1 className="text-3xl font-black tracking-tighter text-zinc-950 dark:text-white mb-2 font-heading">
          Refer & earn
        </h1>
        <p className="text-zinc-500 mb-8">
          Get <span className="font-bold text-brand">{pct}% commission</span> on every referral's first paid month. Plus 5 free credits per signup.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5" data-testid="ref-stat-count">
            <div className="flex items-center gap-1 text-zinc-400 mb-1"><Users className="w-3 h-3" /><span className="text-[10px] font-bold tracking-wider uppercase">Signups</span></div>
            <div className="text-3xl font-black text-zinc-950 dark:text-white font-heading">{stats?.referral_count ?? 0}</div>
          </div>
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5" data-testid="ref-stat-credits">
            <div className="flex items-center gap-1 text-zinc-400 mb-1"><Gift className="w-3 h-3" /><span className="text-[10px] font-bold tracking-wider uppercase">Credits</span></div>
            <div className="text-3xl font-black text-brand font-heading">{stats?.total_credits_earned ?? 0}</div>
          </div>
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-5" data-testid="ref-stat-pending">
            <div className="flex items-center gap-1 text-zinc-400 mb-1"><TrendingUp className="w-3 h-3" /><span className="text-[10px] font-bold tracking-wider uppercase">Pending</span></div>
            <div className="text-3xl font-black text-amber-500 font-heading">{money(stats?.pending_earnings_cents)}</div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-5 text-white" data-testid="ref-stat-total-earnings">
            <div className="flex items-center gap-1 mb-1 opacity-90"><DollarSign className="w-3 h-3" /><span className="text-[10px] font-bold tracking-wider uppercase">Lifetime</span></div>
            <div className="text-3xl font-black font-heading">{money(stats?.total_earnings_cents)}</div>
          </div>
        </div>

        <div className="bg-zinc-950 p-6 md:p-8 mb-8 text-white" data-testid="share-card">
          <div className="flex items-center gap-2 mb-1">
            <Gift className="w-5 h-5 text-brand" />
            <h2 className="text-lg font-bold font-heading">Share these links</h2>
          </div>
          <p className="text-zinc-400 text-sm mb-5">
            The <span className="font-bold text-white">/try link</span> drops people straight into the free demo — highest-converting. The <span className="font-bold text-white">/register link</span> is for closer warm leads.
          </p>

          <div className="space-y-3">
            <div>
              <div className="flex items-center gap-1 mb-1.5">
                <Microscope className="w-3 h-3 text-purple-400" />
                <span className="text-[10px] font-bold tracking-wider uppercase text-purple-400">Demo link (recommended)</span>
              </div>
              <div className="flex items-center gap-2">
                <input readOnly value={tryLink} className="flex-1 bg-zinc-900 border border-zinc-700 px-3 py-2.5 text-white text-sm font-mono truncate" data-testid="referral-try-link" />
                <CopyBtn text={tryLink} label="Copy" className="bg-brand text-white hover:text-white px-4 py-2.5 hover:bg-brand-hover border-brand" />
              </div>
            </div>
            <div>
              <span className="text-[10px] font-bold tracking-wider uppercase text-zinc-500 block mb-1.5">Direct signup link</span>
              <div className="flex items-center gap-2">
                <input readOnly value={signupLink} className="flex-1 bg-zinc-900 border border-zinc-700 px-3 py-2.5 text-zinc-300 text-sm font-mono truncate" data-testid="referral-signup-link" />
                <CopyBtn text={signupLink} label="Copy" className="bg-zinc-800 text-white hover:text-white px-4 py-2.5 hover:bg-zinc-700 border-zinc-700" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-zinc-800">
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent("I built an AI that sounds exactly like me. Free demo, no signup — paste any viral tweet and see the template behind it:")}&url=${encodeURIComponent(tryLink)}`}
              target="_blank"
              rel="noreferrer"
              data-testid="share-twitter-btn"
              className="bg-zinc-900 hover:bg-zinc-800 text-sm font-semibold py-2 text-center border border-zinc-700"
            >
              Share on X
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(tryLink)}`}
              target="_blank"
              rel="noreferrer"
              data-testid="share-linkedin-btn"
              className="bg-zinc-900 hover:bg-zinc-800 text-sm font-semibold py-2 text-center border border-zinc-700"
            >
              LinkedIn
            </a>
            <a
              href={`mailto:?subject=${encodeURIComponent("This AI writes like me")}&body=${encodeURIComponent("Free demo here, no signup:\n\n" + tryLink)}`}
              data-testid="share-email-btn"
              className="bg-zinc-900 hover:bg-zinc-800 text-sm font-semibold py-2 text-center border border-zinc-700"
            >
              Email
            </a>
          </div>
        </div>

        {/* Earnings ledger */}
        {(stats?.recent_earnings?.length ?? 0) > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-bold text-zinc-900 dark:text-white mb-3 font-heading">Cash commissions</h3>
            <div className="space-y-2">
              {stats.recent_earnings.map((e) => (
                <div key={e.id} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-3 flex items-center justify-between" data-testid="earning-row">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-50 dark:bg-green-950 flex items-center justify-center">
                      <DollarSign className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-zinc-900 dark:text-white">{e.referee_email}</p>
                      <p className="text-xs text-zinc-500">{new Date(e.created_at).toLocaleDateString()} · {e.payment_source}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-green-600">{money(e.amount_cents)}</p>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-400">{e.status === "pending_payout" ? "Pending" : e.status}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {stats?.recent_referrals?.length > 0 && (
          <div>
            <h3 className="text-lg font-bold text-zinc-900 dark:text-white mb-3 font-heading">Recent signups</h3>
            <div className="space-y-2">
              {stats.recent_referrals.map((r) => (
                <div key={r.id || r.referred_email} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-3 flex items-center justify-between" data-testid="referral-row">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-brand/5 flex items-center justify-center">
                      <Users className="w-4 h-4 text-brand" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-zinc-900 dark:text-white">{r.referred_email}</p>
                      <p className="text-xs text-zinc-500">{new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-brand">+{r.credits_awarded} cr</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
